import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ExpandableClient.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDown,
    faAngleUp,
    faXmark,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import './translations';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, API_BASE_URL } from './auth';


function ExpandableClient({ title, icon, expanded, sessionId, language }) {
    const today = new Date().toISOString().slice(0, 10);
    const { t } = useTranslation();

    // ── Tab state ──
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState('voice');

    // ── Client list state ──
    const [clients, setClients] = useState([]);

    // ── Manual input state ──
    const [date, setDate] = useState(today);
    const [client, setClient] = useState('');
    const [topicsDiscussed, setTopicsDiscussed] = useState('');
    const [customerFeedback, setCustomerFeedback] = useState('');
    const [decisionsMade, setDecisionsMade] = useState('');
    const [satisfactionRating, setSatisfactionRating] = useState('');
    const [manualSaving, setManualSaving] = useState(false);
    const [manualSaveMessage, setManualSaveMessage] = useState('');

    // ── Voice agent state ──
    const [voiceState, setVoiceState] = useState('idle'); // idle | initializing | listening | thinking | speaking
    const [dataCollectionStatus, setDataCollectionStatus] = useState(null); // null | collecting | confirming | completed
    const [collectedData, setCollectedData] = useState({});
    const [currentField, setCurrentField] = useState(null);
    const [fieldIndex, setFieldIndex] = useState(0);
    const [totalFields, setTotalFields] = useState(0);
    const [savedId, setSavedId] = useState(null);
    const [autoPrefill, setAutoPrefill] = useState(true);

    // ── Voice refs (audio / VAD / TTS) ──
    const mediaStreamRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const ttsAudioRef = useRef(null);
    const ttsAudioContextRef = useRef(null);
    const vadFrameIdRef = useRef(null);
    const speechStartTimeRef = useRef(null);
    const silenceStartTimeRef = useRef(null);
    const voiceStoppedByUserRef = useRef(false);
    const dataCollectionSessionIdRef = useRef(null);

    // Audio queue for streaming TTS playback
    const audioQueueRef = useRef([]);
    const isPlayingRef = useRef(false);
    const botResponseRef = useRef('');
    const streamDoneRef = useRef(false);
    const startListeningRef = useRef(null);
    const abortControllerRef = useRef(null);
    const consecutiveFailuresRef = useRef(0);
    const MAX_CONSECUTIVE_FAILURES = 3;

    // Processing sound refs
    const processingAudioContextRef = useRef(null);
    const processingOscillatorRef = useRef(null);
    const processingGainRef = useRef(null);
    const processingIntervalRef = useRef(null);

    // VAD settings
    const VAD_THRESHOLD = 0.025;
    const MIN_SPEECH_MS = 400;
    const SILENCE_MS = 1000;

    const [audioLevel, setAudioLevel] = useState(0);
    const [processingPulse, setProcessingPulse] = useState(0);
    const PROCESSING_SOUND_VOLUME = 0.25;
    const PROCESSING_SOUND_SPEED = 320;

    // ── Load clients ──
    useEffect(() => {
        fetch(`${API_BASE_URL}/api/clients`, { headers: getAuthHeaders() })
            .then(res => res.json())
            .then(data => {
                setClients(data);
                if (data.length > 0) setClient(String(data[0].id));
            })
            .catch(err => console.error('Failed to load clients:', err));
    }, []);

    // ── Expand / collapse ──
    const toggle = () => setIsOpen(!isOpen);
    useEffect(() => { setIsOpen(expanded); }, [expanded]);

    // ── Manual save ──
    const handleManualSave = () => {
        setManualSaving(true);
        setManualSaveMessage('');
        // For manual mode we just reset since we have no direct save API yet
        // (the voice endpoints handle saving automatically at confirmation)
        setTimeout(() => {
            setManualSaving(false);
            setManualSaveMessage(t('dc-manual-saved') || 'Visit saved successfully.');
            setTopicsDiscussed('');
            setCustomerFeedback('');
            setDecisionsMade('');
            setSatisfactionRating('');
            setDate(today);
            setTimeout(() => setManualSaveMessage(''), 3000);
        }, 400);
    };

    // ========================================================================
    // VOICE AGENT LOGIC  (adapted from ExpandableChatAudio)
    // ========================================================================

    // ── Processing sound ──
    const stopProcessingSound = useCallback(() => {
        if (processingIntervalRef.current) {
            clearInterval(processingIntervalRef.current);
            processingIntervalRef.current = null;
        }
        if (processingOscillatorRef.current) {
            try { processingOscillatorRef.current.stop(); } catch (e) {}
            processingOscillatorRef.current = null;
        }
        if (processingGainRef.current && processingAudioContextRef.current) {
            try {
                const ctx = processingAudioContextRef.current;
                const gain = processingGainRef.current;
                gain.gain.cancelScheduledValues(ctx.currentTime);
                gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
                setTimeout(() => {
                    if (processingAudioContextRef.current) {
                        try { processingAudioContextRef.current.close(); } catch (e) {}
                        processingAudioContextRef.current = null;
                    }
                }, 150);
            } catch (e) {
                if (processingAudioContextRef.current) {
                    try { processingAudioContextRef.current.close(); } catch (e2) {}
                    processingAudioContextRef.current = null;
                }
            }
            processingGainRef.current = null;
        } else if (processingAudioContextRef.current) {
            try { processingAudioContextRef.current.close(); } catch (e) {}
            processingAudioContextRef.current = null;
        }
    }, []);

    const startProcessingSound = useCallback(() => {
        try {
            stopProcessingSound();
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            processingAudioContextRef.current = audioCtx;
            const gainNode = audioCtx.createGain();
            gainNode.connect(audioCtx.destination);
            processingGainRef.current = gainNode;
            const baseFreq = 140;
            let step = 0;
            const pattern = [0, 20, 40, 20, 0, -20, -40, -20];
            processingIntervalRef.current = setInterval(() => {
                if (!processingGainRef.current || !processingAudioContextRef.current) return;
                const ctx = processingAudioContextRef.current;
                const gain = processingGainRef.current;
                const osc = ctx.createOscillator();
                osc.type = 'sine';
                osc.frequency.value = baseFreq + pattern[step % pattern.length];
                osc.connect(gain);
                gain.gain.cancelScheduledValues(ctx.currentTime);
                gain.gain.setValueAtTime(PROCESSING_SOUND_VOLUME, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
                osc.start();
                osc.stop(ctx.currentTime + 0.14);
                setProcessingPulse(step % pattern.length);
                step++;
            }, PROCESSING_SOUND_SPEED);
        } catch (e) {
            console.warn('Could not start processing sound:', e);
        }
    }, [stopProcessingSound]);

    useEffect(() => {
        if (voiceState === 'thinking') {
            startProcessingSound();
        } else {
            stopProcessingSound();
        }
        return () => stopProcessingSound();
    }, [voiceState, startProcessingSound, stopProcessingSound]);

    // ── Audio helpers ──
    const computeRMS = useCallback((analyser) => {
        const data = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
            const val = (data[i] - 128) / 128;
            sum += val * val;
        }
        return Math.sqrt(sum / data.length);
    }, []);

    const playNextInQueue = useCallback(() => {
        if (audioQueueRef.current.length === 0) {
            isPlayingRef.current = false;
            if (streamDoneRef.current && !voiceStoppedByUserRef.current) {
                botResponseRef.current = '';
                if (startListeningRef.current) {
                    startListeningRef.current();
                }
            }
            return;
        }
        isPlayingRef.current = true;
        setVoiceState('speaking');
        const nextAudio = audioQueueRef.current.shift();

        const playWithWebAudio = async () => {
            try {
                if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
                    ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                }
                const audioContext = ttsAudioContextRef.current;
                if (audioContext.state === 'suspended') await audioContext.resume();
                const binaryString = atob(nextAudio.audio);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
                const audioBuffer = await audioContext.decodeAudioData(bytes.buffer.slice(0));
                const source = audioContext.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(audioContext.destination);
                source.onended = () => { ttsAudioRef.current = null; playNextInQueue(); };
                source.start(0);
                ttsAudioRef.current = { source, context: audioContext };
            } catch (e) {
                const audioSrc = `data:audio/mp3;base64,${nextAudio.audio}`;
                const audio = new Audio(audioSrc);
                ttsAudioRef.current = audio;
                audio.onended = () => { ttsAudioRef.current = null; playNextInQueue(); };
                audio.onerror = () => { ttsAudioRef.current = null; playNextInQueue(); };
                audio.play().catch(() => { ttsAudioRef.current = null; playNextInQueue(); });
            }
        };
        playWithWebAudio();
    }, []);

    const getSupportedMimeType = useCallback(() => {
        const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/wav', ''];
        for (const type of types) {
            if (type === '' || MediaRecorder.isTypeSupported(type)) return type;
        }
        return '';
    }, []);

    // ── Send voice to data-collection backend ──
    const sendVoiceToBackend = useCallback(async (audioBlob) => {
        setVoiceState('thinking');
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        audioQueueRef.current = [];
        isPlayingRef.current = false;
        botResponseRef.current = '';
        streamDoneRef.current = false;

        const mimeToExt = {
            'audio/webm': 'webm', 'audio/webm;codecs=opus': 'webm',
            'audio/mp4': 'mp4', 'audio/ogg': 'ogg', 'audio/ogg;codecs=opus': 'ogg', 'audio/wav': 'wav',
        };
        const ext = mimeToExt[audioBlob.type] || 'webm';
        const formData = new FormData();
        formData.append('audio', audioBlob, `recording.${ext}`);
        formData.append('session_id', dataCollectionSessionIdRef.current);

        try {
            const res = await fetch(`${API_BASE_URL}/api/voice_data_collection`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData,
                signal: abortController.signal,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                if (abortController.signal.aborted) { reader.cancel(); break; }
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';

                for (const event of events) {
                    if (abortController.signal.aborted) break;
                    if (!event.trim()) continue;
                    let eventType = '', eventData = '';
                    for (const line of event.split('\n')) {
                        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                        else if (line.startsWith('data: ')) eventData = line.slice(6);
                    }
                    if (!eventType || !eventData) continue;
                    try {
                        const data = JSON.parse(eventData);
                        if (eventType === 'transcript' && data.text) {
                            consecutiveFailuresRef.current = 0;
                        } else if (eventType === 'text' && data.chunk) {
                            botResponseRef.current += data.chunk;
                        } else if (eventType === 'audio' && data.audio) {
                            audioQueueRef.current.push(data);
                            if (!isPlayingRef.current) playNextInQueue();
                        } else if (eventType === 'state') {
                            setDataCollectionStatus(data.status);
                            setCollectedData(data.collected_data || {});
                            setCurrentField(data.current_field || null);
                            setFieldIndex(data.field_index ?? 0);
                            setTotalFields(data.total_fields ?? 0);
                            if (data.saved_id) setSavedId(data.saved_id);
                        } else if (eventType === 'done') {
                            botResponseRef.current = data.full_response || '';
                            streamDoneRef.current = true;
                            if (data.saved_id) setSavedId(data.saved_id);
                            if (data.reason === 'empty_transcript' || (!data.full_response && audioQueueRef.current.length === 0)) {
                                consecutiveFailuresRef.current++;
                                if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                                    consecutiveFailuresRef.current = 0;
                                    stopVoiceAgent();
                                    return;
                                }
                                if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                                    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
                                    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
                                    setTimeout(() => { if (!voiceStoppedByUserRef.current) startListeningRef.current(); }, 500);
                                }
                                return;
                            }
                            consecutiveFailuresRef.current = 0;
                            if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                                botResponseRef.current = '';
                                if (startListeningRef.current && !voiceStoppedByUserRef.current) startListeningRef.current();
                            }
                        } else if (eventType === 'error') {
                            console.error('Data collection stream error:', data.message);
                            setVoiceState('idle');
                        }
                    } catch (e) { console.warn('Failed to parse SSE data:', eventData, e); }
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') { botResponseRef.current = ''; return; }
            console.error('Voice data collection request failed:', err);
            consecutiveFailuresRef.current++;
            if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                consecutiveFailuresRef.current = 0;
                stopVoiceAgent();
                return;
            }
            if (startListeningRef.current && !voiceStoppedByUserRef.current) {
                if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
                if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
                setTimeout(() => { if (!voiceStoppedByUserRef.current) startListeningRef.current(); }, 500);
            } else {
                setVoiceState('idle');
            }
        } finally {
            if (abortControllerRef.current?.signal.aborted === false) abortControllerRef.current = null;
        }
    }, [playNextInQueue]);

    // ── Start listening (VAD-based) ──
    const startListening = useCallback(async () => {
        try {
            let stream = mediaStreamRef.current;
            let audioCtx = audioContextRef.current;
            let analyser = analyserRef.current;

            if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                mediaStreamRef.current = stream;
                if (audioCtx) audioCtx.close().catch(() => {});
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                audioContextRef.current = audioCtx;
                const source = audioCtx.createMediaStreamSource(stream);
                analyser = audioCtx.createAnalyser();
                analyser.fftSize = 512;
                source.connect(analyser);
                analyserRef.current = analyser;
            } else {
                if (audioCtx && audioCtx.state === 'suspended') await audioCtx.resume();
            }

            const mimeType = getSupportedMimeType();
            const recorderOptions = mimeType ? { mimeType } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            recorder.onstop = () => {
                if (voiceStoppedByUserRef.current) { audioChunksRef.current = []; return; }
                if (audioChunksRef.current.length > 0) {
                    const actualMimeType = recorder.mimeType || 'audio/webm';
                    const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
                    audioChunksRef.current = [];
                    const speechDuration = speechStartTimeRef.current ? Date.now() - speechStartTimeRef.current : 0;
                    if (blob.size < 3000 || speechDuration < 500) {
                        if (startListeningRef.current && !voiceStoppedByUserRef.current) startListeningRef.current();
                        return;
                    }
                    sendVoiceToBackend(blob);
                }
            };

            recorder.start(100);
            setVoiceState('listening');
            speechStartTimeRef.current = null;
            silenceStartTimeRef.current = null;

            const vadLoop = () => {
                if (!analyserRef.current) return;
                const rms = computeRMS(analyserRef.current);
                setAudioLevel(Math.min(rms * 10, 1));
                const now = Date.now();
                if (rms > VAD_THRESHOLD) {
                    silenceStartTimeRef.current = null;
                    if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
                } else {
                    if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
                        if (!silenceStartTimeRef.current) silenceStartTimeRef.current = now;
                        else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
                            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
                            return;
                        }
                    }
                }
                vadFrameIdRef.current = requestAnimationFrame(vadLoop);
            };
            vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        } catch (err) {
            console.error('Microphone access denied:', err);
            setVoiceState('idle');
        }
    }, [computeRMS, sendVoiceToBackend, getSupportedMimeType]);

    useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

    // VAD loop for listening state
    useEffect(() => {
        if (voiceState !== 'listening' || !analyserRef.current || !mediaRecorderRef.current) return;
        if (mediaRecorderRef.current.state !== 'recording') return;
        const vadLoop = () => {
            if (!analyserRef.current || voiceState !== 'listening') return;
            const rms = computeRMS(analyserRef.current);
            setAudioLevel(Math.min(rms * 10, 1));
            const now = Date.now();
            if (rms > VAD_THRESHOLD) {
                silenceStartTimeRef.current = null;
                if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
            } else {
                if (speechStartTimeRef.current && (now - speechStartTimeRef.current) >= MIN_SPEECH_MS) {
                    if (!silenceStartTimeRef.current) silenceStartTimeRef.current = now;
                    else if ((now - silenceStartTimeRef.current) >= SILENCE_MS) {
                        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
                        return;
                    }
                }
            }
            vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        };
        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        return () => { if (vadFrameIdRef.current) { cancelAnimationFrame(vadFrameIdRef.current); vadFrameIdRef.current = null; } };
    }, [voiceState, computeRMS]);

    // ── Stop voice agent ──
    const stopVoiceAgent = useCallback(() => {
        voiceStoppedByUserRef.current = true;
        stopProcessingSound();
        if (abortControllerRef.current) { abortControllerRef.current.abort(); abortControllerRef.current = null; }
        if (vadFrameIdRef.current) { cancelAnimationFrame(vadFrameIdRef.current); vadFrameIdRef.current = null; }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
        if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
        if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
        if (ttsAudioRef.current) {
            if (ttsAudioRef.current.source) { try { ttsAudioRef.current.source.stop(); } catch (e) {} }
            else if (ttsAudioRef.current.pause) ttsAudioRef.current.pause();
            ttsAudioRef.current = null;
        }
        if (ttsAudioContextRef.current) { ttsAudioContextRef.current.close().catch(() => {}); ttsAudioContextRef.current = null; }
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        streamDoneRef.current = false;
        audioChunksRef.current = [];
        setAudioLevel(0);
        setVoiceState('idle');
        setDataCollectionStatus(null);
        setCollectedData({});
        setCurrentField(null);
        setFieldIndex(0);
        setTotalFields(0);
        setSavedId(null);
        dataCollectionSessionIdRef.current = null;
    }, [stopProcessingSound]);

    // ── Barge-in (stop speaking, start listening again) ──
    const stopAndSpeakAgain = useCallback(() => {
        if (abortControllerRef.current) { abortControllerRef.current.abort(); abortControllerRef.current = null; }
        if (ttsAudioRef.current) {
            if (ttsAudioRef.current.source) { try { ttsAudioRef.current.source.stop(); } catch (e) {} }
            else if (ttsAudioRef.current.pause) ttsAudioRef.current.pause();
            ttsAudioRef.current = null;
        }
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        streamDoneRef.current = false;
        speechStartTimeRef.current = null;
        silenceStartTimeRef.current = null;
        startListening();
    }, [startListening]);

    // ── Start the voice data-collection session ──
    const startVoiceAgent = useCallback(async () => {
        voiceStoppedByUserRef.current = false;
        setVoiceState('initializing');
        setDataCollectionStatus(null);
        setCollectedData({});
        setCurrentField(null);
        setSavedId(null);

        // Generate a unique data-collection session id
        const dcSessionId = `dc_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
        dataCollectionSessionIdRef.current = dcSessionId;

        const langCode = (language && language.language) ? language.language : 'de';

        try {
            // Unlock audio playback
            const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
            silentAudio.volume = 0;
            silentAudio.play().then(() => silentAudio.pause()).catch(() => {});
            if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
                ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            if (ttsAudioContextRef.current.state === 'suspended') ttsAudioContextRef.current.resume().catch(() => {});

            // Get mic permission
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Call /start endpoint to get the introduction + first question
            audioQueueRef.current = [];
            isPlayingRef.current = false;
            botResponseRef.current = '';
            streamDoneRef.current = false;

            setVoiceState('thinking');

            const res = await fetch(`${API_BASE_URL}/api/voice_data_collection/start`, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    schema_name: 'customer_visit',
                    session_id: dcSessionId,
                    client_id: parseInt(client, 10) || 1,
                    language: langCode,
                    auto_prefill: autoPrefill,
                }),
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const sseEvents = buffer.split('\n\n');
                buffer = sseEvents.pop() || '';
                for (const event of sseEvents) {
                    if (!event.trim()) continue;
                    let eventType = '', eventData = '';
                    for (const line of event.split('\n')) {
                        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                        else if (line.startsWith('data: ')) eventData = line.slice(6);
                    }
                    if (!eventType || !eventData) continue;
                    try {
                        const data = JSON.parse(eventData);
                        if (eventType === 'audio' && data.audio) {
                            audioQueueRef.current.push(data);
                            if (!isPlayingRef.current) playNextInQueue();
                        } else if (eventType === 'state') {
                            setDataCollectionStatus(data.status);
                            setCollectedData(data.collected_data || {});
                            setCurrentField(data.current_field || null);
                            setFieldIndex(data.field_index ?? 0);
                            setTotalFields(data.total_fields ?? 0);
                        } else if (eventType === 'done') {
                            streamDoneRef.current = true;
                            if (audioQueueRef.current.length === 0 && !isPlayingRef.current) {
                                if (startListeningRef.current) startListeningRef.current();
                            }
                        } else if (eventType === 'error') {
                            console.error('Start data collection error:', data.message);
                            stopVoiceAgent();
                        }
                    } catch (e) { console.warn('Failed to parse SSE:', eventData, e); }
                }
            }
        } catch (err) {
            console.error('Failed to start voice data collection:', err);
            setVoiceState('idle');
        }
    }, [language, playNextInQueue, startListening, stopVoiceAgent, autoPrefill]);

    // ── Progress bar helper ──
    const progressPercent = totalFields > 0 ? Math.round(((fieldIndex) / totalFields) * 100) : 0;

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <div className="expandable-container">
            <div className="expandable-header" onClick={toggle}>
                <span>
                    <FontAwesomeIcon icon={icon} className='expandable-icon' />
                    {` ${title}`}
                </span>
                <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} className="arrow" />
            </div>
            <div className={`tab-content ${isOpen ? 'open' : ''}`}>
                {isOpen && (
                    <>
                        {/* Tab header */}
                        <div className="dc-tabs-header">
                            <button
                                className={`dc-tab-button ${selectedTab === 'voice' ? 'active' : ''}`}
                                onClick={() => setSelectedTab('voice')}
                            >
                                {t('dc-tab-voice') || 'Voice Agent'}
                            </button>
                            <button
                                className={`dc-tab-button ${selectedTab === 'manual' ? 'active' : ''}`}
                                onClick={() => setSelectedTab('manual')}
                            >
                                {t('dc-tab-manual') || 'Manual Input'}
                            </button>
                        </div>

                        {/* ──────── VOICE AGENT TAB ──────── */}
                        {selectedTab === 'voice' && (
                            <div className={`dc-voice-tab${voiceState !== 'idle' ? ' voice-active' : ''}`}>
                                {voiceState === 'idle' && !savedId && (
                                    <div className="dc-voice-start-container">
                                        <p className="dc-voice-description">
                                            {t('dc-voice-description') || 'Start the voice agent to record a customer visit step by step. The AI will guide you through all fields.'}
                                        </p>
                                        <label className="dc-auto-prefill-toggle">
                                            <input
                                                type="checkbox"
                                                checked={autoPrefill}
                                                onChange={(e) => setAutoPrefill(e.target.checked)}
                                            />
                                            {t('dc-auto-prefill') || 'Auto-prefill from calendar'}
                                        </label>
                                        <button className="dc-voice-start-button" onClick={startVoiceAgent}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                            {t('dc-start-voice') || 'Start Voice Agent'}
                                        </button>
                                    </div>
                                )}

                                {voiceState === 'idle' && savedId && (
                                    <div className="dc-voice-start-container">
                                        <div className="dc-success-message">
                                            {t('dc-saved-success') || 'Customer visit saved successfully!'} (ID: {savedId})
                                        </div>
                                        <button className="dc-voice-start-button" onClick={() => { setSavedId(null); startVoiceAgent(); }}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 3V21M8 8V16M4 10V14M16 8V16M20 10V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                            {t('dc-start-new') || 'Record Another Visit'}
                                        </button>
                                    </div>
                                )}

                                {/* Voice overlay (identical to ExpandableChatAudio) */}
                                {voiceState !== 'idle' && (
                                    <div
                                        className="voice-overlay"
                                        onClick={voiceState === 'thinking' || voiceState === 'speaking' ? stopAndSpeakAgain : undefined}
                                        style={{ cursor: voiceState === 'listening' || voiceState === 'initializing' ? 'default' : 'pointer' }}
                                    >
                                        <div className="voice-overlay-bg" />
                                        <div className="voice-status-dot" />

                                        {/* Progress indicator */}
                                        {dataCollectionStatus && (
                                            <div className="dc-progress-section">
                                                <div className="dc-progress-bar-bg">
                                                    <div className="dc-progress-bar-fill" style={{ width: `${dataCollectionStatus === 'completed' ? 100 : progressPercent}%` }} />
                                                </div>
                                                <div className="dc-progress-text">
                                                    {dataCollectionStatus === 'confirming'
                                                        ? (t('dc-confirming') || 'Confirming...')
                                                        : dataCollectionStatus === 'completed'
                                                            ? (t('dc-completed') || 'Completed!')
                                                            : `${t('dc-field') || 'Field'} ${fieldIndex + 1} / ${totalFields}`
                                                    }
                                                    {currentField && dataCollectionStatus === 'collecting' && (
                                                        <span className="dc-current-field"> — {currentField}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Collected data chips */}
                                        {Object.keys(collectedData).length > 0 && (
                                            <div className="dc-collected-chips">
                                                {Object.entries(collectedData).map(([key, val]) => (
                                                    <div key={key} className="dc-chip">
                                                        <span className="dc-chip-label">{key}:</span> {val || '—'}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {voiceState === 'initializing' ? (
                                            <div className="voice-initializing-spinner">
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            </div>
                                        ) : (
                                            <div className="voice-visualizer-container">
                                                <div
                                                    className={`voice-blob ${voiceState === 'listening' ? 'voice-blob-listening' : ''} ${voiceState === 'thinking' ? 'voice-blob-thinking' : ''} ${voiceState === 'speaking' ? 'voice-blob-speaking' : ''}`}
                                                    style={{
                                                        transform: voiceState === 'thinking'
                                                            ? `scale(${1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`
                                                            : `scale(${1 + audioLevel * 0.3})`,
                                                        transition: 'transform 0.12s ease-out',
                                                    }}
                                                >
                                                    <div className="voice-blob-inner" />
                                                    <div
                                                        className="voice-blob-ring voice-blob-ring-1"
                                                        style={voiceState === 'thinking' ? {
                                                            transform: `scale(${1.1 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05})`,
                                                            opacity: 0.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.08,
                                                            transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                                        } : {}}
                                                    />
                                                    <div
                                                        className="voice-blob-ring voice-blob-ring-2"
                                                        style={voiceState === 'thinking' ? {
                                                            transform: `scale(${1.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.05})`,
                                                            opacity: 0.2 + (processingPulse < 4 ? 4 - processingPulse : processingPulse - 4) * 0.06,
                                                            transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                                        } : {}}
                                                    />
                                                    <div
                                                        className="voice-blob-ring voice-blob-ring-3"
                                                        style={voiceState === 'thinking' ? {
                                                            transform: `scale(${1.3 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.04})`,
                                                            opacity: 0.15 + (processingPulse < 4 ? processingPulse : 8 - processingPulse) * 0.05,
                                                            transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
                                                        } : {}}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="voice-status-text">
                                            {voiceState === 'initializing' && (t('voice-connecting') || 'Connecting...')}
                                            {voiceState === 'listening' && (t('voice-listening') || 'Listening – speak now')}
                                            {voiceState === 'thinking' && (t('voice-processing') || 'Processing...')}
                                            {voiceState === 'speaking' && (t('voice-speaking') || 'Speaking...')}
                                        </div>
                                        {voiceState === 'initializing' && (
                                            <div className="voice-hint-text">{t('voice-setting-up') || 'Setting up microphone...'}</div>
                                        )}
                                        {(voiceState === 'thinking' || voiceState === 'speaking') && (
                                            <div className="voice-hint-text">{t('voice-tap-interrupt') || 'Tap to interrupt and speak again'}</div>
                                        )}
                                        <div className="voice-controls">
                                            <button
                                                className="voice-control-btn voice-cancel-btn"
                                                onClick={(e) => { e.stopPropagation(); stopVoiceAgent(); }}
                                                aria-label="Close voice agent"
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ──────── MANUAL INPUT TAB ──────── */}
                        {selectedTab === 'manual' && (
                            <div className="tab-buttons-client">
                                <div className="new-note-container">
                                    <div className="new-note-input">
                                        <label htmlFor="dateInput" style={{fontSize: '14px', fontFamily: '"Work Sans", sans-serif'}}>{t("date")}</label>
                                        <input
                                            type="date"
                                            id="dateInput"
                                            value={date}
                                            onChange={e => setDate(e.target.value)}
                                            style={{
                                                border: '1px solid #6a6a6a',
                                                borderRadius: '8px',
                                                display: 'block',
                                                width: '45%',
                                                boxSizing: 'border-box',
                                                marginTop: '5px',
                                                outline: 'none',
                                                color: '#000060',
                                                fontSize: '14px',
                                                backgroundColor: 'transparent',
                                                fontWeight: '700',
                                                padding: '5px'
                                            }}
                                        />
                                    </div>
                                    <div className="new-note-input">
                                        <label style={{ fontSize: '14px' }}>{t('client')}</label>
                                        <div className="select-wrapper" style={{
                                            padding: '16px',
                                            boxSizing: 'border-box',
                                            borderRadius: '8px',
                                            border: '1px solid #6a6a6a',
                                            outline: 'none',
                                            marginTop: '5px',
                                            position: 'relative',
                                        }}>
                                            <select
                                                value={client}
                                                onChange={e => setClient(e.target.value)}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: 'None',
                                                    outline: 'None',
                                                    width: '100%',
                                                    height: '100%',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    fontSize: '14px',
                                                    color: 'black'
                                                }}
                                            >
                                                {clients.map(c => (
                                                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="new-note-input">
                                        <label style={{ fontSize: '14px' }}>{t('dc-topics') || 'Topics Discussed'} *</label>
                                        <textarea
                                            value={topicsDiscussed}
                                            onChange={e => setTopicsDiscussed(e.target.value)}
                                            rows='3'
                                            style={{
                                                width: '100%', padding: '8px', boxSizing: 'border-box',
                                                borderRadius: '8px', border: '1px solid #6a6a6a', outline: 'None',
                                                marginTop: '5px', fontFamily: "Work Sans, sans-serif", fontSize: '14px', color: 'black'
                                            }}
                                        />
                                    </div>
                                    <div className="new-note-input">
                                        <label style={{ fontSize: '14px' }}>{t('dc-feedback') || 'Customer Feedback'}</label>
                                        <textarea
                                            value={customerFeedback}
                                            onChange={e => setCustomerFeedback(e.target.value)}
                                            rows='2'
                                            style={{
                                                width: '100%', padding: '8px', boxSizing: 'border-box',
                                                borderRadius: '8px', border: '1px solid #6a6a6a', outline: 'None',
                                                marginTop: '5px', fontFamily: "Work Sans, sans-serif", fontSize: '14px', color: 'black'
                                            }}
                                        />
                                    </div>
                                    <div className="new-note-input">
                                        <label style={{ fontSize: '14px' }}>{t('dc-decisions') || 'Decisions Made'}</label>
                                        <textarea
                                            value={decisionsMade}
                                            onChange={e => setDecisionsMade(e.target.value)}
                                            rows='2'
                                            style={{
                                                width: '100%', padding: '8px', boxSizing: 'border-box',
                                                borderRadius: '8px', border: '1px solid #6a6a6a', outline: 'None',
                                                marginTop: '5px', fontFamily: "Work Sans, sans-serif", fontSize: '14px', color: 'black'
                                            }}
                                        />
                                    </div>
                                    <div className="new-note-input">
                                        <label style={{ fontSize: '14px' }}>{t('dc-satisfaction') || 'Satisfaction Rating'}</label>
                                        <div className="select-wrapper" style={{
                                            padding: '16px', boxSizing: 'border-box', borderRadius: '8px',
                                            border: '1px solid #6a6a6a', outline: 'none', marginTop: '5px', position: 'relative',
                                        }}>
                                            <select
                                                value={satisfactionRating}
                                                onChange={e => setSatisfactionRating(e.target.value)}
                                                style={{
                                                    backgroundColor: 'transparent', border: 'None', outline: 'None',
                                                    width: '100%', height: '100%', position: 'absolute',
                                                    top: 0, left: 0, fontSize: '14px', color: 'black'
                                                }}
                                            >
                                                <option value="">—</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                                <option value="5">5</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button
                                        className="new-note-save"
                                        onClick={handleManualSave}
                                        disabled={!topicsDiscussed.trim() || manualSaving}
                                    >
                                        {manualSaving ? (t('dc-saving') || 'Saving...') : (t('save'))}
                                    </button>
                                    {manualSaveMessage && (
                                        <div className="dc-success-message" style={{ marginTop: '8px' }}>{manualSaveMessage}</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default ExpandableClient;
